// AdminFestivalForm.jsx
import React, { useEffect, useState } from 'react'
import {
  Box,
  TextField,
  Button,
  Typography,
  Snackbar,
  Alert,
  Paper,
  Container,
  CircularProgress,
} from '@mui/material'

// import your api helpers (adjust path if needed)
import { getCurrentFestival, setCurrentFestival } from '../../services/api' // <- update this path to where your api.js exports live

export default function AdminFestivalForm() {
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [snack, setSnack] = useState({ open: false, severity: 'info', text: '' })

  // new: current live greeting fetched from server
  const [currentGreeting, setCurrentGreeting] = useState(null)
  const [loadingCurrent, setLoadingCurrent] = useState(true)

  // Fetch current greeting on mount
  useEffect(() => {
    let mounted = true
    setLoadingCurrent(true)
    getCurrentFestival()
      .then((res) => {
        if (!mounted) return
        setCurrentGreeting(res?.data?.message || null)
      })
      .catch((err) => {
        console.error('Failed to load current festival message', err)
        if (!mounted) return
        setCurrentGreeting(null)
      })
      .finally(() => mounted && setLoadingCurrent(false))

    return () => {
      mounted = false
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!message || message.trim().length < 3) {
      setSnack({ open: true, severity: 'warning', text: 'Please enter a valid greeting (min 3 chars).' })
      return
    }

    setLoading(true)
    try {
      const res = await setCurrentFestival(message.trim()) // axios returns a promise
      const saved = res?.data?.message || message.trim()
      setSnack({ open: true, severity: 'success', text: `Greeting updated: "${saved}"` })
      setCurrentGreeting(saved)
      setMessage('')
    } catch (err) {
      console.error('Failed to set festival message:', err)
      const msg = err?.response?.data?.error || err.message || 'Unknown error'
      setSnack({ open: true, severity: 'error', text: `Failed to update greeting — ${msg}` })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container
      maxWidth="sm"
      sx={{
        mt: { xs: 14, sm: 18 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '70vh',
      }}
    >
      <Paper
        elevation={4}
        sx={{
          p: 4,
          width: '100%',
          background: 'linear-gradient(180deg, #121212 0%, #1c1c1c 100%)',
          borderRadius: 3,
          border: '1px solid rgba(255,255,255,0.06)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.6)',
        }}
      >
        <Typography variant="h5" fontWeight={700} color="white" gutterBottom align="center">
          ✨ Festival Greetings
        </Typography>
        <Typography variant="body2" color="gray" align="center" mb={3}>
          Add or update the festival message shown on your website.
        </Typography>

        {/* Current live greeting */}
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Typography variant="caption" display="block" color="text.secondary" gutterBottom>
            Current live greeting
          </Typography>

          {loadingCurrent ? (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 1 }}>
              <CircularProgress size={18} thickness={5} />
              <Typography variant="body2" color="text.secondary">Loading...</Typography>
            </Box>
          ) : currentGreeting ? (
            <Paper
              elevation={0}
              sx={{
                display: 'inline-block',
                px: 2.5,
                py: 1,
                borderRadius: 2,
                background: 'rgba(255, 183, 77, 0.04)',
                border: '1px solid rgba(255,183,77,0.08)',
              }}
            >
              <Typography variant="body2" sx={{ color: '#ffd085', fontWeight: 700 }}>
                Rockstar Bot Trading Wishes You a {currentGreeting}
              </Typography>
            </Paper>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No greeting set
            </Typography>
          )}
        </Box>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Greeting message"
            placeholder="e.g. Happy Diwali!"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ style: { color: '#aaa' } }}
            sx={{
              input: { color: 'white' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#ffb74d' },
                '&.Mui-focused fieldset': { borderColor: '#ffa726' },
              },
            }}
            helperText="This message will appear on the main site (English only)."
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              sx={{
                background: 'linear-gradient(90deg, #ffb74d, #ff8a00)',
                color: '#111',
                fontWeight: 700,
                px: 4,
                '&:hover': { background: 'linear-gradient(90deg, #ffc166, #ff9b2a)' },
              }}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Greeting'}
            </Button>

            <Button
              variant="outlined"
              onClick={() => {
                if (!message.trim()) {
                  setSnack({ open: true, severity: 'warning', text: 'Enter a message to preview' })
                  return
                }
                setSnack({
                  open: true,
                  severity: 'info',
                  text: `Preview: Rockstar Bot Trading Wishes You a ${message.trim()}`,
                })
              }}
              sx={{
                borderColor: '#ffb74d',
                color: '#ffb74d',
                fontWeight: 600,
                px: 4,
                '&:hover': { borderColor: '#ff9800', color: '#ff9800' },
              }}
            >
              Preview
            </Button>
          </Box>
        </Box>

        <Snackbar
          open={snack.open}
          autoHideDuration={5000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: '100%' }}>
            {snack.text}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  )
}
